// We had to copy this file from @react-aria/button because of the console.warn
// once they fix it we can remove this file and use the original one

import { useFocusable } from '@react-aria/focus'
import { usePress } from '@react-aria/interactions'
import { mergeProps } from '@react-aria/utils'
import type { AriaButtonProps as BaseAriaButtonProps } from '@react-types/button'
import type { DOMAttributes, FocusableElement } from '@react-types/shared'
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ElementType,
  HTMLAttributes,
  InputHTMLAttributes,
  RefObject,
} from 'react'

export type TAriaButtonProps<T extends ElementType = 'button'> = BaseAriaButtonProps<T> & {
  /** Whether text selection should be enabled on the pressable element. */
  allowTextSelectionOnPress?: boolean
}

type TButtonAria<T> = {
  /** Props for the button element. */
  buttonProps: T
  /** Whether the button is currently pressed. */
  isPressed: boolean
}

// Order with overrides is important: 'button' should be default
export function useAriaButton(
  props: TAriaButtonProps,
  ref: RefObject<HTMLButtonElement>,
): TButtonAria<ButtonHTMLAttributes<HTMLButtonElement>>
export function useAriaButton(
  props: TAriaButtonProps<'a'>,
  ref: RefObject<HTMLAnchorElement>,
): TButtonAria<AnchorHTMLAttributes<HTMLAnchorElement>>
export function useAriaButton(
  props: TAriaButtonProps<'div'>,
  ref: RefObject<HTMLDivElement>,
): TButtonAria<HTMLAttributes<HTMLDivElement>>
export function useAriaButton(
  props: TAriaButtonProps<'input'>,
  ref: RefObject<HTMLInputElement>,
): TButtonAria<InputHTMLAttributes<HTMLInputElement>>
export function useAriaButton(
  props: TAriaButtonProps<'span'>,
  ref: RefObject<HTMLSpanElement>,
): TButtonAria<HTMLAttributes<HTMLSpanElement>>
export function useAriaButton(
  props: TAriaButtonProps<ElementType>,
  ref: RefObject<FocusableElement>,
): TButtonAria<DOMAttributes>
/**
 * Provides the behavior and accessibility implementation for a button component. Handles mouse, keyboard, and touch interactions,
 * focus behavior, and ARIA props for both native button elements and custom element types.
 * @param props - Props to be applied to the button.
 * @param ref - A ref to a DOM element for the button.
 */
export function useAriaButton(
  props: TAriaButtonProps<ElementType>,
  ref: RefObject<FocusableElement>,
): TButtonAria<HTMLAttributes<object>> {
  const {
    elementType = 'button',
    isDisabled,
    onPress,
    onPressStart,
    onPressEnd,
    onPressChange,
    // @ts-expect-error - undocumented
    preventFocusOnPress,
    // @ts-expect-error - undocumented
    allowFocusWhenDisabled,
    // @ts-expect-error - undocumented
    onClick: deprecatedOnClick,
    href,
    target,
    rel,
    type = 'button',
    allowTextSelectionOnPress,
  } = props
  let additionalProps

  if (elementType === 'button') {
    additionalProps = {
      type,
      disabled: isDisabled,
    }
  } else {
    additionalProps = {
      role: 'button',
      tabIndex: isDisabled ? undefined : 0,
      href: elementType === 'a' && isDisabled ? undefined : href,
      target: elementType === 'a' ? target : undefined,
      type: elementType === 'input' ? type : undefined,
      disabled: elementType === 'input' ? isDisabled : undefined,
      'aria-disabled': !isDisabled || elementType === 'input' ? undefined : isDisabled,
      rel: elementType === 'a' ? rel : undefined,
    }
  }

  const { pressProps, isPressed } = usePress({
    onPressStart,
    onPressEnd,
    onPressChange,
    onPress,
    isDisabled,
    preventFocusOnPress,
    allowTextSelectionOnPress,
    ref,
  })

  const { focusableProps } = useFocusable(props, ref)

  const updatedFocusableProps =
    allowFocusWhenDisabled && isDisabled ? { ...focusableProps, tabIndex: -1 } : focusableProps
  const buttonProps = mergeProps(updatedFocusableProps, pressProps, props)

  return {
    isPressed, // Used to indicate press state for visual
    buttonProps: mergeProps(additionalProps, buttonProps, {
      'aria-haspopup': props['aria-haspopup'],
      'aria-expanded': props['aria-expanded'],
      'aria-controls': props['aria-controls'],
      'aria-pressed': props['aria-pressed'],
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        deprecatedOnClick?.(e)
      },
    }),
  }
}
